import { each, filter as _filter, isNull, union, uniq, compact, map as _map } from 'lodash';
import { METERS_PER_UNIT } from 'ol/proj/Units';
import OlFeature from 'ol/Feature';
import { GeoJSON as OlGeoJSON } from 'ol/format';
import OlCircle from 'ol/geom/Circle';
import { Point, MultiPolygon, Polygon } from 'ol/geom';
import {
    fromCircle as OlPolygonFromCircle,
    fromExtent as OlPolygonFromExtent
} from 'ol/geom/Polygon';
// point selection
import Contains from 'ol/format/filter/Contains';
// polygon/circle selection
import Intersects from 'ol/format/filter/Intersects';
// Turf
import booleanDisjoint from '@turf/boolean-disjoint';
import booleanWithin from '@turf/boolean-within';

import { fromJson } from '../../../services/utilities';
import { isInRange } from '../../../services/map';
import {
    OWS_TYPE,
    NEDGLOBE_MAP_RESOLUTION,
    NEDGLOBE_MAP_SCALES,
    INCHES_PER_METER,
    NEDGLOBE_MAP_PROJECTION,
    DEFAULT_DOTS_PER_INCH,
    FEEDBACKS_GEOMETRY_TYPE_CODES,
    POINT_TOLERANCE_DIAMETER_IN_MM,
    APPLICATION_JSON,
    MAX_RESULTS_RELATION,
    FIELD_TYPE,
    NG_LAYER_TYPES
} from '../../../constants';

import { GetVersionAndOutput, getFeatures } from '../layer/wfs/wfsVectorLayer';
import { expandFilterValue, FilterObject, SEARCH_FILTER, ATTRIBUTE_FILTER } from '../filter';
import { isSmallDevice } from '../../utilities/devices';

export function getFeatureInfoForViewPort(store, map, layerData, maxFeatures) {
    const bbox = map.getView().calculateExtent(map.getSize());
    return getFeatureInfoForBBox(store, map, layerData, bbox, maxFeatures);
}

export async function getFeatureInfoForBBox(store, map, layerData, bbox, maxFeatures, forceLoading = false, cancellationToken = null) {
    // bbox to polygon
    const polygon = OlPolygonFromExtent(bbox);
    return getFeatureInfoForPolygon(store, map, layerData, polygon, maxFeatures, forceLoading, cancellationToken);
}

export async function getFeatureInfoForPolygon(store, map, layerData, polygon, maxFeatures, forceLoading = false, cancellationToken = null) {
    if (layerData.type === NG_LAYER_TYPES.CUSTOM) {
        const olLayer = store.getters.getMapLayer(null, layerData.id)?.olLayer;
        return olLayer?.getSource().getFeaturesInExtent(polygon.getExtent()).map(feature => feature.clone());
    }
    const userData = store.getters.getUserData;
    if (layerData.extended.informationAttributes?.length === 0) {
        return [];
    } else {
        const sourceVersion = getSourceVersion(layerData);
        const srsName = NEDGLOBE_MAP_PROJECTION.getCode();

        const source = store.getters.getSourceById(layerData.data.source.id);

        let options = {
            // WFS/WMS/WMTS version
            sourceVersion: sourceVersion,
            // other properties
            featureTypes: [layerData.extended.layerName],
            outputFormat: source?.wfsOutputFormat ?? APPLICATION_JSON,
            geometryName: layerData.extended.geometryColumn,
            // PBI 13931: Dashboard: Ondersteunen OWS met aparte URL's voor WMS, WMTS en WFS
            // Do not provide a list of property names.
            // Otherwise the id is ommited bij de mapserver.
            // To include the property names, use: this.cleanArray(this.getColumns()),
            propertyNames: [],
            isFeedback: layerData.extended.feedbackTypeId > 0,
            maxFeatures: maxFeatures + 1,
            filters: [],
            intersectsFilter: useIntersectsFilter(map),
            srsName,
            sortProperties: layerData.extended.sortProperties
        };

        if (layerData.extended.layerName === 'atlas:Recording') {
            options.maxFeatures = 5000;
        }

        // Add bbox if WFS only supports a bbox spatial operator
        if (layerData.extended.useBboxSpatialOperator) {
            options.intersectsFilter = false;
            options.bbox = polygon.getExtent();
        } else {
            // spatial filter
            options.filter = new Intersects(layerData.extended.geometryColumn, polygon, srsName);
        }
        // other filters
        options = setFeedbackOptions(options, layerData.extended, userData);
        options = UpdateOptionsFilters(layerData, options);

        const selectionResult = await findFeatures(store, layerData, options, true, null, 0, forceLoading, cancellationToken);
        return getFeaturesForPointSelection(selectionResult.data);
    }
}

function getSourceVersion(layerData) {
    if (layerData.extended.serviceType === 'WFS') {
        return layerData.extended.source.sourceWfsVersion;
    } else if (layerData.extended.serviceType === 'WMS') {
        return layerData.extended.source.sourceWmsVersion;
    } else if (layerData.extended.serviceType === 'WMTS') {
        return layerData.extended.source.sourceWtsVersion;
    }
    return 1.1; // fallback version
}

export function layerFeaturesCanBePaged(layerData) {
    return layerData.extended.serviceType === 'WFS' && getSourceVersion(layerData) >= 2;
}

async function findFeatures(store, layerData, options, loader, timeout, startIndex = 0, forceLoading = false, cancellationToken = null) {
    if (cancellationToken && store.getters.getCheckForCancelToken(cancellationToken)) {
        store.commit('DEACTIVATE_SELECTION_CANCELLATION_TOKEN', cancellationToken);
        return null;
    }
    if (loader) {
        store.dispatch('setLoader', { loading: true });
    }
    // by default we are going to request features, but if the startindex exceeds
    // the organizations maxFeatures, we require confirmation from the user
    let continueLoading = true;
    // only page for >v2 wfs
    const usePaging = layerFeaturesCanBePaged(layerData);
    const count = layerData.extended.countDefault || options.maxFeatures - 1;

    let selectionResult = await getSelectionResults(store, layerData, options, count, timeout, startIndex);

    const resultFeatures = selectionResult?.data?.features;

    if (usePaging && resultFeatures?.length >= count) {
        const newStartIndex = startIndex + count;
        if (newStartIndex >= MAX_RESULTS_RELATION && (forceLoading === null)) {
            continueLoading = false;
        } else if (forceLoading === false) {
            // forceLoading indicates (the user allready has chosen) to load all results
            // if startindex is greater than or equeal to organization maxfeatures,
            // ask once to continue
            if (newStartIndex >= (options.maxFeatures - 1)) {
                const msg = (selectionResult.data.numberMatched !== undefined) ?
                    'maxResultsExceededContinueConfirmWithCount' : 'maxResultsExceededContinueConfirm';
                const userResponse = await store.dispatch('askConfirm', {
                    message: msg,
                    messageVars: [layerData.extended.title, options.maxFeatures - 1, selectionResult.data.numberMatched]
                });
                forceLoading = null;
                continueLoading = userResponse === true;
            }
        }

        // only stop if the confirm is denied
        if (continueLoading !== false) {
            // get all results
            const nextResult = await findFeatures(store, layerData, options, loader, timeout, newStartIndex, forceLoading, cancellationToken);
            const nextResultFeatures = nextResult.data?.features.concat(resultFeatures);
            selectionResult.data.features = nextResultFeatures;
        }
    }
    if (loader) {
        store.dispatch('setLoader', { loading: false });
    }

    // For web feedbacklayers log email as AVG data
    if (layerData.type === 'feedback') {
        logAvgData(store, selectionResult, layerData.data.feedbackId);
    }

    return selectionResult;
}

function logAvgData(store, selectionResult, feedbackId) {
    // get feedbacklayer from store
    const feedback = store.getters.getFeedbackById(feedbackId);
    // Only for web feedbacks
    if (feedback.data.feedbackType.useInWebTheme) {
        // And find an email field and get the name property
        const emailField = feedback.data.feedbackType.feedbackFields.filter(f => f.personalData && f.type === FIELD_TYPE.EMAIL)[0]?.name;

        // if email is found log all emails found in selected messages.
        // to avoid double logs create a set of unique email values
        if (emailField) {
            let set = new Set();
            selectionResult.data.features.forEach((feature) => {
                const email = feature.properties[emailField];
                set.add(email);
            });

            set.forEach((email) => {
                // Log email naar avgLog
                const avgLogData = {
                    Action: 'Web Feedback',
                    Email: email
                };

                store.dispatch('logAvgUsage', {
                    avgLogData: avgLogData
                });
            });
        }
    }
}

async function getSelectionResults(store, layerData, options, count, timeout, startIndex) {
    // If layerData.data is available and serviceType= WMS get results from WMS service.
    if (layerData.data?.serviceType === OWS_TYPE.WMS) {
        // Only get featureinfo if layer showFeatureInfo is set to true
        if (layerData.data.showFeatureInfo) {
            return await getWmsResults(store, layerData, options, false);
        }
    } else {
        return getPagedresults(store, layerData, options, false, timeout, count, startIndex);
    }

    return null;
}

async function getWmsResults(store, layerData, options, useLoader) {
    let sourceFormat = null;
    if (layerData?.data?.source?.id) {
        sourceFormat = store.getters.getSourceById(layerData.data.source.id);
    }
    if (options.filter.geometry instanceof Point) {
        // WMS GetFeatureInfo request
        const getFeatureInfoData = {
            themeLayerId: layerData.extended.id,
            coordinates: options.filter.geometry.flatCoordinates,
            format: sourceFormat?.wmsOutputFormat ?? APPLICATION_JSON,
        };

        return store.dispatch('requestLayerFeatureInfo', { data: getFeatureInfoData, loader: useLoader });
    }

    // return an empty result in case of a polygon selection
    // wms GetFeatureInfo can only handle a single point request
    return getEmptyWmsResult(layerData.data.layerName);
}

/**
 * Empty WMS result in case of a polygon selection.
 * WMS GetFeatureInfo only support point selections.
 */
function getEmptyWmsResult(layerName) {
    return {
        data: {
            type: 'FeatureCollection',
            numberMatched: 0,
            name: layerName,
            features: [],
        }
    };
}

async function getPagedresults(store, layerData, options, useLoader, timeout, count, startIndex) {
    const xmlSerializedFeatures = getFeatures(options, count, startIndex);

    if (layerData.extended?.bagService) {
        return store.dispatch('requestBagServiceFeatures', { data: xmlSerializedFeatures, loader: useLoader });
    }

    return store.dispatch('requestLayerFeatures', { data: xmlSerializedFeatures, loader: useLoader, layerId: layerData.extended.id });
}

export async function getBagVboFeatureInfoFromFilters(store, data, isAddress = true) {
    let filters = data.map(item => {
        const defaults = {
            caseSensitive: true,
            operator: '=',
            visible: true,
            dataType: 'xsd:string'
        };
        if (isAddress) {
            return [
                {
                    filterAttribute: 'postcode',
                    value: item?.postcode,
                    ...defaults
                },
                {
                    filterAttribute: 'huisnummer',
                    value: item?.huisnummer,
                    ...defaults
                },
                {
                    filterAttribute: 'huisletter',
                    value: item?.huisletter,
                    ...defaults
                },
                {
                    filterAttribute: 'toevoeging',
                    value: item?.toevoeging,
                    ...defaults
                }
            ];
        } else {
            return [
                {
                    ...item,
                    ...defaults
                }
            ];
        }
    });
    const multipleFilters = filters.length > 1;
    if (!multipleFilters) {
        filters = filters.flatMap(filter => filter);
    }

    const layerData = {
        extended: {
            bagService: true,
            layerName: 'bag:verblijfsobject',
            geometryName: 'geom',
            serviceType: 'WFS',
            source: { sourceWfsVersion: '2' }
        }
    };
    const userData = store.getters.getUserData;
    const bbox = undefined;
    return getFeatureInfoFromFilters(
        store,
        layerData,
        filters,
        bbox,
        userData,
        multipleFilters
    );
}

export async function getFeatureInfoFromFilters(store, layerData, filterObjects, bbox, userData, multipleFilters = false) {
    // by default use geometryColumn for geometryName, overridden for feedbacks
    const { layerName, geometryColumn: geometryName, serviceType, source, feedbackId } = layerData.extended;
    const { maxFeatures } = userData;
    const createFilterObjectFunction = (filterObject) => FilterObject.AttributeFilterObject({
        caseSensitive: filterObject.caseSensitive,
        filterAttribute: filterObject.filterAttribute,
        operator: filterObject.operator,
        value: expandFilterValue(filterObject.value),
        visible: filterObject.visible,
        dataType: filterObject.dataType
    });

    let filters = [];
    if (multipleFilters) {
        filterObjects.forEach((filter) => {
            filters.push(filter.map(createFilterObjectFunction));
        });
    } else {
        filters = filterObjects.map(createFilterObjectFunction);
    }

    let sourceVersion = 1.1; // fallback version
    if (serviceType === 'WFS') {
        sourceVersion = source.sourceWfsVersion;
    } else if (serviceType === 'WMS') {
        sourceVersion = source.sourceWmsVersion;
    } else if (serviceType === 'WMTS') {
        sourceVersion = source.sourceWtsVersion;
    }

    let sourceFormat = null;
    if (layerData?.data?.source?.id) {
        sourceFormat = store.getters.getSourceById(layerData.data.source.id);
    }

    let options = {
        // WFS/WMS/WMTS version
        sourceVersion: sourceVersion,
        // other properties
        featureTypes: [layerName],

        // If outputformat is in source use that value, default otherwise
        outputFormat: sourceFormat?.wfsOutputFormat ?? APPLICATION_JSON,
        geometryName,
        propertyNames: [],
        bbox,
        isFeedback: !!feedbackId,
        maxFeatures: maxFeatures + 1,
        filters,
        intersectsFilter: false,
        srsName: NEDGLOBE_MAP_PROJECTION.getCode(),
        sortProperties: layerData.extended.sortProperties
    };
    options = setFeedbackOptions(options, layerData.extended, userData);
    const selectionResult = await findFeatures(store, layerData, options, true);
    return getFeaturesForPointSelection(selectionResult.data);
}

export async function getFeatureInfoAtPosition(store, map, layerObject, position, resolution) {
    const layerData = layerObject.extended;
    const { tolerance, correctedTolerance } = getTolerance(store, layerData, map, resolution);

    // Selection box around the position with the given tolerance
    const geometry = GetSelectionBox(position, correctedTolerance);

    if (layerObject.type === NG_LAYER_TYPES.CUSTOM) {
        const olLayer = store.getters.getMapLayer(null, layerObject.id)?.olLayer;
        const features = olLayer?.getSource().getFeaturesInExtent(geometry).map(feature => feature.clone());
        return getFeaturesForPointSelection(features, position, correctedTolerance, false);
    }

    if (!layerData.informationAttributes || layerData.informationAttributes.length === 0) {
        return [];
    }

    const cleanedColumnArray = getColumns(layerData)
        .filter(column => column && column.length > 0);

    if (cleanedColumnArray.length <= 0) {
        return [];
    }

    // Get options for wfs.getFeatures() request
    const filters = layerObject.filters.settings.map(({ filter }) => filter);

    let optionsWithWfsOptions = null;

    if (tolerance === 0) {
        // get options for point selection
        optionsWithWfsOptions = GetWfsPointOptions(map, layerData, position, store.getters.getUserData, filters);
    } else {
        // get options for polygon selection
        optionsWithWfsOptions = GetWfsPolygonOptions(map, layerData, geometry, store.getters.getUserData, filters);
    }


    if (optionsWithWfsOptions.outputFormat === APPLICATION_JSON) {
        // Function wfsVectorLayer.js GetVersionAndOutput sets an output format 'json' for a specific WFS service
        // we do not want to overwrite that format here. So only try and get other value is it's application/json
        // Get outputformat from source
        const source = store.getters.getSourceById(layerData.sourceId);

        // If outputformat is in source use that value, default otherwise
        optionsWithWfsOptions.outputFormat = source?.wfsOutputFormat ?? APPLICATION_JSON;
    }

    // sort properties
    optionsWithWfsOptions.sortProperties = layerObject.extended.sortProperties;

    // Add filters to options
    const optionsWithFilters = UpdateOptionsFilters(layerData, optionsWithWfsOptions);

    // Add bbox if WFS only supports a bbox spatial operator and add geometryname
    if (layerData.useBboxSpatialOperator) {
        optionsWithFilters.bbox = geometry;
        optionsWithFilters.geometryName=optionsWithFilters.filter.geometryName;
    }
    const selectionResult = await findFeatures(store, layerObject, optionsWithFilters);
    if (selectionResult?.data) {
        if (layerData.selectionAttribute) {
            // selection on attribute value
            return getFeaturesForPointSelection(selectionResult.data);
        } else {
            // selection on position
            return getFeaturesForPointSelection(selectionResult.data, position, correctedTolerance);
        }
    }
    return [];
}

function getTolerance(store, layerData, map, resolution) {
    let feedbackTolerance;

    if (layerData.feedbackTypeId) {
        const feedbackObject = store.getters.getFeedbackById(layerData.feedbackId);
        const geometryType = feedbackObject.data.feedbackType.geometryType;
        const isLineOrPoint = geometryType.includes(FEEDBACKS_GEOMETRY_TYPE_CODES.POINT) || geometryType.includes(FEEDBACKS_GEOMETRY_TYPE_CODES.LINE);
        feedbackTolerance = isLineOrPoint ? POINT_TOLERANCE_DIAMETER_IN_MM : 0;
    }

    // For WMS services Tolerance = 0.
    // However they are stored as 8 sometimes due to bug #54203 .
    let tolerance = 0;
    if (layerData.serviceType != OWS_TYPE.WMS) {
        tolerance = feedbackTolerance ?? layerData.tolerance;
    }
    // tolerantie instelling voor de thema laag.
    // geef vlakken met tolerantie 0 een kleine tolerantie om een selectie cirkel te kunnen maken.
    // geef punten/lijnen een tolerantie gebaseerd op wereld coordinaten, niet de absolute tolerantie op het scherm.
    // tolerantie is de selectie diameter op het scherm, hier gaan we uit van de radius, de helft van de diameter.
    const correctedTolerance = (tolerance === 0) ? 0.001 : calculateWorldDistance(map, tolerance / 1000.0, resolution) / 2.0;
    return {
        tolerance,
        correctedTolerance,
    };
}

function useIntersectsFilter(map) {
    return false;
}

function getColumns(layerData) {
    // Check if style has a text attribute.
    let textAttributes = [];
    // Do we have labels specified?
    textAttributes = getLabelAttributes(layerData);

    // If not use the text attribute specified in style
    if (textAttributes.length === 0) {
        each(_filter(layerData.styles, function (style) {
            return (!isNull(style.textAttributeDefault) || !isNull(style.textAttributeSelection) || !isNull(style.textAttributeSelected));
        }), function (attribute) {
            textAttributes.push(attribute.textAttributeDefault);
            textAttributes.push(attribute.textAttributeSelection);
            textAttributes.push(attribute.textAttributeSelected);
        });
    }

    textAttributes = compact(uniq(textAttributes));

    // Welke kolommen ophalen?
    return union(compact(uniq(_map(getAttributes(layerData), 'attribute'))), [layerData.geometryColumn], textAttributes);
}

/**
* Voeg de filters toe aan het options.filters array
* @param  {object} layerData, themalaag object
* @param  {object} options, object met een filters veld
* @return {object} aangepast options object
*/
function UpdateOptionsFilters(layerData, options) {
    // voeg de laagfilters toe aan het options.filters array
    const createdOptions = { ...options };
    const filterObjects = getFilters(layerData);
    if (filterObjects) {
        // voeg elke attributefilter toe aan de options.filter for WFS request
        for (const filterObject of filterObjects) {
            createdOptions.filters.push(filterObject);
        }
    }
    return createdOptions;
}

function getFilters(layerData) {
    return layerData.filters?.settings
        ?.map(filterObject => filterObject.filter)
        .filter(filterItem => {
            return filterItem.type === ATTRIBUTE_FILTER || filterItem.type === SEARCH_FILTER;
        });
}

function GetWfsPointOptions(map, layerData, position, userData, filters) {
    const options = GetWfsPointOptionsObject(map, layerData, position, filters);
    return setFeedbackOptions(options, layerData, userData);
}

/**
* Verkrijg het options object nodig voor WFS.getFeatures(options) aanroep.
*
* @param {type} self      The object
* @param {type} geometry  The geometry
* @return {type} The wfs options.
*/
function GetWfsPolygonOptions(map, layerData, geometry, userData, filters) {
    const options = GetWfsPolygonOptionsObject(map, layerData, geometry, filters);
    return setFeedbackOptions(options, layerData, userData);
}

/**
* If the layer is a feedbacklayer 1) Sets the geometryName, and 2) prefix the layername with the namespace
* @param {any} options - the WFS options for creating the WFS GetFeature request
* @param {any} feedback - the feedback, contains the organization id needed for namespace prefix
*/
function setFeedbackOptions(options, layerData, userData) {
    const { organizationId } = userData;
    // Change options if this is a themelayer based on a feedback
    const optionsWithFeedbackProperties = { ...options };
    if (layerData.feedbackTypeId > 0) {
        // Set geometry name
        optionsWithFeedbackProperties.geometryName = 'geolocation';
        // Remove properties that are unknown to the user
        optionsWithFeedbackProperties.propertyNames = options.propertyNames.filter(function (e) {
            return e !== 'geolocation' && e !== 'gpslocation';
        });
        // Prefix with org namespace if available, i.e. org2:feedback_rontestlijn

        if (organizationId && !!layerData.feedbackId && optionsWithFeedbackProperties.featureTypes) {
            let i;
            for (i = 0; i < optionsWithFeedbackProperties.featureTypes.length; i++) {
                const ft = optionsWithFeedbackProperties.featureTypes[i];
                if (isFeedbackFeatureType(ft)) {
                    optionsWithFeedbackProperties.featureTypes[i] = `org${organizationId}:${ft}`;
                }
            }
        }
    }
    return optionsWithFeedbackProperties;
}

function isFeedbackFeatureType(featureType) {
    return featureType.startsWith('feedback_') || featureType.startsWith('webfeedback_');
}

/**
* Get the WFS filter options object
*
* @param {type} layerData       The object
* @param {type} selBox            The selection box geometry
* @param {array} filters        Filters array, default []
* @return {type} The wfs options.
*/
function GetWfsPolygonOptionsObject(map, layerData, selBox, filters = []) {
    let options = GetWfsCommonOptionsObject(map, layerData, filters);
    options.geometryName = layerData.geometryColumn;
    // selBox to polygon
    const polygon = OlPolygonFromExtent(selBox);
    const srsName = NEDGLOBE_MAP_PROJECTION.getCode();
    options.filter = new Intersects(options.geometryName, polygon, srsName);
    return options;
}

function GetWfsPointOptionsObject(map, layerData, position, filters = []) {
    let options = GetWfsCommonOptionsObject(map, layerData, filters);
    // add contains filter
    const geometry = new Point(position, 'XY');
    const srsName = NEDGLOBE_MAP_PROJECTION.getCode();
    options.filter = new Contains(layerData.geometryColumn, geometry, srsName);
    return options;
}

function GetWfsCommonOptionsObject(map, layerData, filters = []) {
    const version = GetVersionAndOutput(layerData);
    return {
        featureTypes: [layerData.layerName],
        outputFormat: version.output,
        version: layerData.feedback ? '2.0.0' : version.version,
        // PBI 13931: Dashboard: Ondersteunen OWS met aparte URL's voor WMS, WMTS en WFS
        // Do not provide a list of property names.
        // Otherwise the id is ommited bij de mapserver.
        // To include the property names, use: this.cleanArray(this.getColumns()),
        propertyNames: [],
        isFeedback: layerData.feedbackTypeId > 0,
        filters: filters,
        intersectsFilter: useIntersectsFilter(map),
        srsName: NEDGLOBE_MAP_PROJECTION.getCode(),
        sortProperties: []
    };
}

/**
 * Get a boundingbox at postition with a tolerance around it.
 * @param {[type]} position  [position/midppoint of the selBox]
 * @param {[type]} tolerance [distance from the position to the edge of the selBox]
 */
function GetSelectionBox(position, tolerance) {
    const geometry = [];
    geometry.push(position[0] - tolerance);
    geometry.push(position[1] - tolerance);
    geometry.push(position[0] + tolerance);
    geometry.push(position[1] + tolerance);
    return geometry;
}

export function calculateWorldDistance(map, screenDistanceInM, resolution) {
    const scale = getScale(map, resolution);
    // return world distance
    return screenDistanceInM * scale;
}

function getScale(map, resolution) {
    if (!resolution) {
        resolution = getCurrentResolution(map);
    }

    let index = NEDGLOBE_MAP_RESOLUTION.findIndex(item => {
        return item === resolution;
    });

    // If scale is decreased by 0.01 check if the scale can be reapplied
    if (isNaN(NEDGLOBE_MAP_SCALES[index]) || typeof NEDGLOBE_MAP_SCALES[index] === 'undefined') {
        index = NEDGLOBE_MAP_RESOLUTION.findIndex(item => {
            return item === resolution + 0.01;
        });
    }

    // Smartphone / iPad can zoom inbetween regular zoomlevels (pinch-zoom)
    // If the resolution/scale are not in the expected array, calculate the scale based on DPI
    return index !== -1 ? NEDGLOBE_MAP_SCALES[index] : getDPIBasedScale(map, resolution);
}

function getCurrentResolution(map) {
    return map.getView().getResolution();
}

function getDPIBasedScale(map, currentResolution) {
    const unit = map.getView().getProjection().getUnits();
    return currentResolution * METERS_PER_UNIT[unit] * INCHES_PER_METER * DEFAULT_DOTS_PER_INCH;
}

// Determines whether the getFeature request should be executed based on layer attributes and resolution
export function shouldGetFeatureInfo(layerData, resolution, isWeb, webShowTableView, showTableViewSmallDevices, checkLayerAttributes = true) {
    if (checkLayerAttributes) {
        // Skip getFeature if layer doesn't have information-attributes
        if (getAttributes(layerData, isWeb, webShowTableView, showTableViewSmallDevices).length === 0) {
            return false;
        }
    }
    // Skip getFeature if layer is not in range, otherwise do getFeature-request
    return isInRange(resolution, layerData.minResolution, layerData.maxResolution);
}

function getAttributes(layerData, isWeb, webShowTableView, showTableViewSmallDevices) {
    let attributes = [];
    const feedback = layerData.feedbackTypeId > 0;
    if (feedback) {
        attributes = layerData.informationAttributes.filter(attribute => {
            return displayFeedbackAttribute(layerData.feedbackFields, attribute, isWeb);
        });
    } else {
        attributes = layerData.informationAttributes.filter(attribute => {
            return displayThemeAttribute(attribute, isWeb, webShowTableView, showTableViewSmallDevices);
        });
    }
    return attributes;
}

function getLabelAttributes(layerData) {
    let attributes = [];
    attributes = layerData.informationAttributes.filter(attribute => {
        return isTextAttribute(attribute);
    });
    return attributes;
}

export function isTextAttribute(attribute) {
    return attribute.isTextAttribute;
}

export function displayThemeAttribute(attribute, isWeb = false, webShowTableView = false, showTableViewSmallDevices = false) {
    // Return popup attributes for small devices
    if (isSmallDevice()) {
        if (showTableViewSmallDevices) {
            return attribute.displayInTable;
        }
        return attribute.popup;
    } else {
        // Return popup attributes for web where 'Toon tabelweergave' is switched off, display in table otherwise
        if (isWeb && !webShowTableView) {
            return attribute.popup;
        }
        return attribute.displayInTable;
    }
}

function displayFeedbackAttribute(feedbackFields, attribute, isWeb) {
    const feedbackField = feedbackFields.find(ff => {
        return ff.name === attribute.attribute;
    });
    if (feedbackField && isWeb) {
        return !feedbackField.personalData;
    }
    // Return all attributes for a feedback in Mobile
    // All fields can be modified, including personal data and invisible fields
    return true;
}

// Get the features for a point selection
export function getFeaturesForPointSelection(result, position, tolerance, isJson = true) {
    const geojson = new OlGeoJSON();
    return isJson ? getFeaturesFromJSON(geojson, result, position, tolerance) : getFeaturesFromNative(result, position, tolerance);
}

function getFeaturesFromJSON(geojson, validatedJSON, position, tolerance) {
    const features = [];
    each(geojson.readFeatures(fromJson(validatedJSON)), (feature) => {
        // geometry may not always be available
        const geometry = feature.getGeometry();
        if (geometry) {
            const geometryType = geometry.getType();
            if (geometryType !== 'Point' && geometryType != 'LineString' && position && tolerance) {
                // Remove features with no intersect
                const circle = new OlCircle(position, tolerance);
                const geom = new OlPolygonFromCircle(circle);

                // Remove features with no intersect, as GetFeature request was done with bounding-box
                if (featureDisjointFromGeom(geojson, geom, feature)) {
                    return;
                }
            }
        }
        features.push(feature);
    });
    return features;
}

function getFeaturesFromNative(features, position, tolerance) {
    const geojson = new OlGeoJSON();
    return features.filter(feature => {
        if (position && tolerance) {
            // Remove features with no intersect
            const circle = new OlCircle(position, tolerance);
            const geom = new OlPolygonFromCircle(circle);

            // Remove features with no intersect, as GetFeature request was done with bounding-box
            if (featureDisjointFromGeom(geojson, geom, feature)) {
                return false;
            }
        }
        return true;
    });
}

// Returns true if the intersection of the two geometries is an empty set
function featureDisjointFromGeom(geojson, geom, feature) {
    const selectionAsGeoJSON = geojson.writeFeatureObject(new OlFeature(geom));
    try {
        // some polygons contain multipolygons
        // some shapes could not be selected (#1423)
        // first try to create seperate polygons and test for overlap
        // if fails fall back to original method
        const featureGeometry = feature.getGeometry();
        const coords = featureGeometry.getCoordinates();
        const overlapBooleans = coords.map(coord => {
            const polygon = new Polygon([coord]);
            const featureAsGeoJSON = geojson.writeFeatureObject(new OlFeature({ geometry: polygon }));
            return booleanDisjoint(selectionAsGeoJSON, featureAsGeoJSON);
        });
        return !overlapBooleans.includes(false);
    } catch (err) {
        const featureAsGeoJSON = geojson.writeFeatureObject(feature);
        return booleanDisjoint(selectionAsGeoJSON, featureAsGeoJSON);
    }
}

export function fixDateFormats(featureFromMap) {
    if (this.feedback) {
        featureFromMap = this.processFeedback(featureFromMap);
    } else {
        const props = featureFromMap.properties;

        for (const prop of props) {
            if (prop.value !== undefined) {
                if (this.PropIsDateTime(prop)) {
                    featureFromMap = this.FixDateTime(prop, featureFromMap);
                } else if (this.PropIsDate(prop)) {
                    featureFromMap = this.FixDate(prop, featureFromMap);
                }
            }
        }
    }
    return featureFromMap;
}

// Get the features within geometry or overlapping geometry
export function getFeaturesForGeometrySelection(result, geometry, within) {
    const geojson = new OlGeoJSON();
    if (within) {
        return result
            .filter(feature => {
                if (feature.getGeometry() instanceof Point) {
                    return !featureDisjointFromGeom(geojson, geometry, feature);
                }
                return featureWithinGeom(geojson, geometry, feature);
            });
    }
    return result
        .filter(feature => !featureDisjointFromGeom(geojson, geometry, feature));
}

function featureWithinGeom(geojson, geom, feature) {
    const selectionAsGeoJSON = geojson.writeFeatureObject(new OlFeature(geom));
    const featureGeometry = feature.getGeometry();
    if (featureGeometry instanceof MultiPolygon) {
        const polygons = featureGeometry.getPolygons();
        const multiOverlaps = polygons
            .map(polygon => {
                const polygonFeature = new OlFeature({ geometry: polygon });
                const polygonFeatureAsGeoJSON = geojson.writeFeatureObject(polygonFeature);
                return booleanWithin(polygonFeatureAsGeoJSON, selectionAsGeoJSON);
            });
        const hasFoundOutside = multiOverlaps.includes(false);
        return !hasFoundOutside;

    }
    const featureAsGeoJSON = geojson.writeFeatureObject(feature);
    return booleanWithin(featureAsGeoJSON, selectionAsGeoJSON);
}
